import React, { useEffect, useMemo, useState } from 'react';
import { SealCheck, Star } from '@phosphor-icons/react';

import { Icon } from '../../../design-system/components/Icon';
import {
  Card,
  CardHeader,
  Pagination,
  Rating,
  SectionTitle,
  SkeletonLines,
} from '../../../design-system/components/desktop';
import { ConsoleEmpty, ConsoleError } from '../../../design-system/patterns/console';
import { useViewport } from '../../../design-system/hooks/useViewport';

import '../../Dashboard/dashboard.css';
import './reviews.css';

/**
 * Recent reviews, the view. Pure.
 *
 * THE BUG THIS FIXES
 * ------------------
 * The old file rendered FIVE hardcoded `<StarIcon color="#FACC14" fill="#FACC14">`
 * and never read `review.rating` at all, so every review on the page looked
 * like a five star review no matter what the seller actually scored. It is the
 * shared `Rating` now, reading the real value, with an explicit `outOf` so it
 * cannot pick up the divide-by-20 bug the other copies of `renderStars` carry.
 *
 * Stars are INK, never yellow, per `README.md` section 8. The gold was the only
 * saturated colour on the page and it outranked the accent, which is the
 * system's state colour.
 *
 * The API returns `rating` on some review payloads and not others. A review
 * with no rating renders its text and no stars rather than a zero-star row,
 * because "not rated" and "rated zero" are different facts and only one of them
 * is true.
 *
 * PAGED AT FOUR. The container asks for up to a hundred reviews in one request
 * and this used to render every one of them, so a seller with forty reviews got
 * a forty-review wall in a dashboard panel and everything below it was pushed
 * off the screen. Four is about a panel's worth.
 *
 * The paging is LOCAL and this stays pure: the rows are already in memory, so
 * a page change is a slice and not a fetch, and the explorer can still mount
 * this with fixture props and page through them.
 *
 * A RAIL BELOW THE HANDOFF, AND NO PAGER WITH IT
 * ----------------------------------------------
 * On a phone the paginated column costs a full screen for four reviews and
 * then asks for a tap to see the next four. Mobile gets the same reviews as
 * cards in one horizontally swipeable row instead (`reviews.css`).
 *
 * THE PAGER DOES NOT COME WITH IT. A pager over a rail is a second control for
 * the same axis: the thumb swipes right, hits the end of a four-card rail, and
 * the fifth review is behind a tap on a different control doing the same
 * movement in a different idiom. So the rail carries the WHOLE list and the
 * pager stays a desktop control. Nothing is hidden by that — there is no
 * all-reviews route to send a "see all" to, and the list is already bounded by
 * the container's own request, which asks for at most a hundred.
 *
 * This branches on `useViewport()` rather than a media query because the two
 * tracks render different DOM, not the same DOM differently: mobile mounts
 * every review and no pager, desktop mounts a slice of four and a pager.
 *
 * CARDS ON BOTH TRACKS
 * --------------------
 * Desktop used to be hairline-separated rows, on the rule in `dashboard.css`
 * that a review is a row in a list and must not get a surface inside a surface.
 * That rule was written for a stack of rows of one KIND — orders, listings —
 * where the hairline is the whole separation. A review is not that: it is a
 * quoted block of somebody else's words with an attribution and a score, and
 * four of them stacked on hairlines read as one paragraph of run-on text. The
 * bordered card is what says where one person stops speaking and the next
 * starts, which is exactly the argument the mobile rail already made. It is a
 * border and not a tone lift for the reason `reviews.css` gives: the panel is
 * already `--bg-card` and every candidate ground collides with it in one theme.
 *
 * The pager stays on desktop and only there, unchanged.
 */
const PAGE_SIZE = 4;

export function RecentReviewsView({ reviews = [], loading = false, error, onRetry }) {
  const { isDesktop } = useViewport();
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(reviews.length / PAGE_SIZE));

  // A shorter list can strand the reader on a page that no longer exists —
  // a filter upstream, a retry that returns less, a review deleted.
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const visible = useMemo(
    () => (isDesktop ? reviews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : reviews),
    [reviews, page, isDesktop],
  );

  return (
    <Card>
      <CardHeader>
        <SectionTitle>Recent reviews</SectionTitle>
      </CardHeader>

      {loading ? (
        <SkeletonLines rows={4} />
      ) : error ? (
        /* `inline` because both of these sit inside a dashboard `Card` beside
           three others, not under a full table. That no longer means "glyph
           instead of artwork" — `Empty` now reads `inline` as "same artwork,
           one size down" (xs, not the 112px default), so the error and empty
           states of this panel stay the same picture as everywhere else and
           still fit the panel. See `illustrations/README.md`. */
        <ConsoleError inline error={error} onRetry={onRetry} title="Could not load your reviews" />
      ) : reviews.length === 0 ? (
        <ConsoleEmpty
          inline
          /* Stated rather than left to `ConsoleEmpty`'s default, which happens
             to be this same slug. Reviews ARE the ledger the scenario
             describes — it fills as buyers act, and the seller is owed
             nothing by its being empty — so the default is right by meaning
             and not by luck, and saying so stops it silently changing if the
             default ever moves. */
          illustration="no-orders-yet"
          title="No reviews yet"
          text="Buyers can review you once an order is delivered. Their reviews appear here."
        />
      ) : (
        <>
        {/* The rail is a scrollable region with no focusable children, which
            leaves a keyboard user unable to reach the reviews past the second
            card. `tabIndex={0}` makes it a scroll stop; the group name is what
            a screen reader announces when they land on it. The column needs
            neither, because it scrolls with the page. */}
        <div
          className={
            isDesktop ? 'dsa-dash__reviews dsa-reviews-cards' : 'dsa-reviews-rail'
          }
          {...(isDesktop
            ? {}
            : { tabIndex: 0, role: 'group', 'aria-label': 'Recent reviews' })}
        >
          {visible.map((review, index) => {
            const rating = Number(review.rating);
            const hasRating = Number.isFinite(rating) && rating > 0;

            return (
              <article
                className="dsa-dash__review"
                key={review.id ?? index}
                data-testid={`card-review-${index}`}
              >
                <div className="dsa-dash__review-head">
                  <div className="dsa-dash__review-who">
                    <span className="dsa-dash__review-name" data-testid={`text-reviewer-${index}`}>
                      {review.user || 'Buyer'}
                    </span>
                    {review.verified ? (
                      <span
                        className="dsa-dash__review-verified"
                        title="Verified purchase"
                        aria-label="Verified purchase"
                      >
                        <Icon as={SealCheck} size="sm" active />
                      </span>
                    ) : null}
                  </div>

                  <div className="dsa-dash__review-meta">
                    {hasRating ? (
                      <Rating value={rating} outOf={5} size="sm" showValue />
                    ) : (
                      <span className="dsa-dash__review-when">
                        <Icon as={Star} size="sm" /> Not rated
                      </span>
                    )}
                    {review.date ? (
                      <span className="dsa-dash__review-when">{review.date}</span>
                    ) : null}
                  </div>
                </div>

                {review.review ? (
                  <p className="dsa-dash__review-text" data-testid={`text-review-${index}`}>
                    {review.review}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>

        {isDesktop && pageCount > 1 ? (
          <Pagination
            className="dsa-dash__reviews-pager"
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
            totalItems={reviews.length}
            pageSize={PAGE_SIZE}
            itemNoun="reviews"
          />
        ) : null}
        </>
      )}
    </Card>
  );
}

export default RecentReviewsView;
